var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { ContextualHelpNotification } from '../ContextualHelpNotification';
import { FormFieldHelper } from '../FormFieldHelper';
import { Space } from '../Space';
import { extractFormFieldHelperProps } from './utils';
import { CharacterCounter } from '../CharacterCounter';
import { formTextBackgroundStyles } from './context';
var InlineContextualHelp = styled(ContextualHelpNotification)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var space = _a.theme.space;
    return "\n    display: inline-flex;\n    width: fit-content;\n    height: 44px;\n    align-items: center;\n    margin-left: ".concat(space.xs, ";\n    padding-top: 3px;\n  ");
});
var StyledCharacterCounter = styled(CharacterCounter)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var $isDisabled = _a.$isDisabled, colors = _a.theme.colors;
    return "\n    color: ".concat(colors['slate-5'], ";\n    ").concat($isDisabled ? "color: ".concat(colors['periwinkle-500'], ";") : '', "\n    ").concat($isDisabled ? 'cursor: not-allowed;' : '', "\n  ");
});
var CharacterCounterWrapper = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  ", "\n\n  ", "\n"], ["\n  ", "\n\n  ", "\n"])), function (_a) {
    var space = _a.theme.space, $marginRight = _a.$marginRight;
    return "\n      position: absolute;\n      bottom: ".concat(space.xxs, ";\n      padding: 1px;\n      margin-right: ").concat($marginRight, "px;\n      border-radius: 1px;\n      right: 0;\n    ");
}, formTextBackgroundStyles);
export var useFormText = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var isHovered = _a.isHovered, props = __rest(_a, ["isHovered"]);
    var variant = props.variant, labelContent = props.labelContent, errorNotification = props.errorNotification, className = props.className, internalRef = props.internalRef;
    var staticId = useRef(uuidv4()).current;
    var charCounterId = useRef("field-counter-".concat(uuidv4())).current;
    var characterCounterRef = useRef(null);
    var _h = __read(useState(), 2), stackedDescribedBy = _h[0], setStackedDescribedBy = _h[1];
    var formFieldHelperProps = useMemo(function () { return extractFormFieldHelperProps(props); }, [props]);
    var formFieldHelper = useMemo(function () { return (React.createElement(Space, { mt: "xs" },
        React.createElement(FormFieldHelper, __assign({ onDescribedByChange: setStackedDescribedBy }, formFieldHelperProps)))); }, [formFieldHelperProps, setStackedDescribedBy]);
    var inlineContextualHelp = useMemo(function () {
        if (!props.contextualHelp ||
            props.contextualHelp.variant !== 'inline' ||
            !props.contextualHelp.content)
            return null;
        var _a = props.contextualHelp, content = _a.content, customId = _a.customId;
        var helpId = customId || "help-id-".concat(staticId);
        return {
            id: helpId,
            element: React.createElement(InlineContextualHelp, { id: helpId, content: content }),
        };
    }, [props.contextualHelp, staticId]);
    var displayCounter = useMemo(function () {
        var _a;
        return !!(charCounterId &&
            charCounterId.length &&
            typeof props.maxLength === 'number' &&
            props.maxLength > 0 &&
            ((_a = props.characterCounter) === null || _a === void 0 ? void 0 : _a.show));
    }, [charCounterId, (_b = props.characterCounter) === null || _b === void 0 ? void 0 : _b.show, props.maxLength]);
    var characterCounter = useMemo(function () {
        var _a, _b, _c;
        return (React.createElement(CharacterCounterWrapper, { "$variant": variant, ref: characterCounterRef, "$isHovered": isHovered || false, "$isDisabled": props.disabled || false, "$marginRight": Math.ceil((((_a = internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0) -
                (((_b = internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth) || 0) +
                8) },
            React.createElement(StyledCharacterCounter, { id: ((_c = props.characterCounter) === null || _c === void 0 ? void 0 : _c.customId) || charCounterId, charCount: props.value.length, maxLength: props.maxLength || 0, hideAppendedText: true, "$isDisabled": props.disabled || false })));
    }, [
        variant,
        isHovered,
        props.disabled,
        (_c = props.characterCounter) === null || _c === void 0 ? void 0 : _c.customId,
        props.value.length,
        props.maxLength,
        charCounterId,
    ]);
    return {
        variant: variant,
        labelContent: labelContent,
        className: className,
        describedBy: "".concat(stackedDescribedBy || '').concat(((_d = props.characterCounter) === null || _d === void 0 ? void 0 : _d.show) || ((_e = props.characterCounter) === null || _e === void 0 ? void 0 : _e.customId)
            ? " ".concat(((_f = props.characterCounter) === null || _f === void 0 ? void 0 : _f.customId) || charCounterId)
            : ''),
        formTextId: props.id || "mui-text-field-".concat(staticId),
        hasError: !!(errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.message) || !!(errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customId),
        isDisabled: !!props.disabled,
        formFieldHelper: formFieldHelper,
        inlineContextualHelp: (inlineContextualHelp === null || inlineContextualHelp === void 0 ? void 0 : inlineContextualHelp.element) || null,
        characterCounter: displayCounter ? characterCounter : undefined,
        characterCounterWidth: 
        // the + 15 accounts for 8px padding to the right of the character counter
        // and gives an extra 2px padding to the left of it
        Math.ceil(((_g = characterCounterRef.current) === null || _g === void 0 ? void 0 : _g.getBoundingClientRect().width) || 0) + 15,
    };
};
var templateObject_1, templateObject_2, templateObject_3;
//# sourceMappingURL=useFormText.js.map